import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnFilterHeader,
  formatDateFilterValue,
  matchAmountFilter,
  matchGymIdFilter,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { matchSaleHistoryDateFilter } from '../../receptionist/sales/saleHistoryFilters';
import PageHeader from '../../../components/owner/PageHeader';
import { useAuth } from '../../../context/AuthContext';
import { usePanelToast } from '../../../context/PanelToastContext';
import DailyZReport from '../../shared/DailyZReport';
import DailyZReport1 from '../../shared/DailyZReport1';
import {
  formatCurrency,
  ACCOUNT_SIDES,
  ACCOUNT_METHODS,
  suggestNextAccountCode,
  buildZReport,
  localDateKey,
  normalizeTenderMethod,
  normalizeLedgerGroup,
  normalizeAccountMethod,
  saleCategoryFromPayment,
  SALE_CATEGORY_ORDER,
  loadManualCashPayments,
  addManualCashPayment,
  deleteManualCashPayment,
  suggestNextCashPaymentCode,
  loadManualBankPayments,
  addManualBankPayment,
  deleteManualBankPayment,
  suggestNextBankPaymentCode,
  loadManualCardPayments,
  addManualCardPayment,
  deleteManualCardPayment,
  suggestNextCardPaymentCode,
  loadManualQrPayments,
  loadManualExpenses,
  addManualExpense,
  deleteManualExpense,
  suggestNextExpenseCode,
  loadExpenseCategories,
  loadExpenseCategoryRecords,
  addExpenseCategory,
  deleteExpenseCategory,
  syncExpenseCategoryRecords,
  mapApiExpenseToManual,
  mergeManualExpenses,
  clearManualExpensesCache,
  getExpenseCategoryOptions,
  formatExpenseCategoryCodeRange,
  compareExpenseCodes,
  buildSubscriptionAccountRows,
  buildCoaSalesEntryRowsFromFinance,
  financePaymentPool,
} from './financeData';
import {
  fetchFinanceLedgerData,
  createExpense,
  deleteExpense,
  fetchExpenseCategories,
  createExpenseCategoryRemote,
  deleteExpenseCategoryRemote,
  createChartAccount,
  updateChartAccount,
  removeChartAccount,
  fetchCoaZReportExpenses,
  EXPENSE_CATEGORIES,
} from '../../../services/admin/financeLedgerApi';
import { setActiveGymId } from '../../../services/receptionist/gymContext';
import { peekGymCache } from '../../../services/cache/gymCache';
import { invalidateCache } from '../../../services/cache/queryCache';
import {
  exportFinanceCsv,
  exportFinanceExcel,
  exportFinancePdf,
  exportFinance,
  printFinanceList,
} from './exportFinance';
import CoaAccountTree from './CoaAccountTree';
import JournalLedger from './JournalLedger';
import VoucherPage from './vouchers/VoucherPage';
import { VOUCHER_TYPES } from './vouchers/voucherTypes';
import CustomerBalanceList from './CustomerBalanceList';
import { PAYMENTS_UPDATE_EVENT } from '../../receptionist/paymentStore';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './finance.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const NEON_GREEN = '#94FF00';
const TEXT_SECONDARY = '#94a3b8';
const GRID_COLOR = 'rgba(148, 163, 184, 0.08)';

const TABS = [
  { id: 'accounts', label: 'Chart of Accounts' },
  { id: 'journal', label: 'Journal Ledger' },
  ...VOUCHER_TYPES.map((v) => ({ id: v.tabId, label: v.shortLabel })),
  { id: 'customer-balance', label: 'Customer Balance' },
  { id: 'zreport', label: 'Z Report' },
  { id: 'zreport1', label: 'Z Report 1' },
];

const VOUCHER_TAB_IDS = new Set(VOUCHER_TYPES.map((v) => v.tabId));

function PaymentBadge({ status }) {
  const map = {
    paid: 'owner-badge-success',
    pending: 'owner-badge-info',
    overdue: 'owner-badge-warning',
    unpaid: 'owner-badge-warning',
    completed: 'owner-badge-success',
    applied: 'owner-badge-warning',
    accrued: 'owner-badge-info',
    refunded: 'owner-badge-warning',
    active: 'owner-badge-success',
    inactive: 'owner-badge-warning',
    expired: 'owner-badge-warning',
  };
  const label = String(status || '—');
  return (
    <span className={`owner-badge ${map[label.toLowerCase()] || 'owner-badge-info'}`}>
      {label.charAt(0).toUpperCase() + label.slice(1)}
    </span>
  );
}

function EmptyRow({ cols, message = 'No records found' }) {
  return (
    <tr>
      <td colSpan={cols} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
        {message}
      </td>
    </tr>
  );
}

function TenderPaymentsTable({
  title,
  rows,
  sourceRows,
  loading,
  emptyMessage,
  filters,
  openKey,
  toggleFilter,
  updateFilter,
  methodLabel,
}) {
  const total = rows.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  return (
    <div className="owner-card rec-panel-card admin-finance-section">
      <div className="admin-finance-member-header">
        <h2 className="owner-card-title">{title}</h2>
        <p className="owner-placeholder-text" style={{ margin: 0 }}>
          {rows.length} payments · {formatCurrency(total)}
        </p>
      </div>
      <div className="owner-table-wrap">
        <table className="owner-table rec-member-table admin-list-table admin-finance-table">
          <thead>
            <tr>
              <ColumnFilterHeader
                label="Payment ID"
                filterKey="id"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search ID..."
              />
              <ColumnFilterHeader
                label="Serial No"
                filterKey="gymId"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search serial..."
              />
              <ColumnFilterHeader
                label="Member"
                filterKey="member"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search member..."
              />
              <ColumnFilterHeader
                label="Type"
                filterKey="type"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search type..."
              />
              <ColumnFilterHeader
                label="Amount"
                filterKey="amount"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search amount..."
              />
              <ColumnFilterHeader
                label="Date"
                filterKey="date"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="DD-MM-YYYY"
                formatValue={formatDateFilterValue}
              />
              <ColumnFilterHeader
                label={methodLabel}
                filterKey="method"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search method..."
              />
              <ColumnFilterHeader
                label="Status"
                filterKey="status"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search status..."
              />
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((p) => (
              <tr key={`${p.id}-${p.date}-${p.amount}`}>
                <td>{p.id}</td>
                <td className="rec-member-gym-id">{p.gymId || '—'}</td>
                <td>{p.member || '—'}</td>
                <td>{p.type || '—'}</td>
                <td className={Number(p.amount) < 0 ? 'finance-amount-negative' : 'finance-amount-positive'}>
                  {formatCurrency(p.amount)}
                </td>
                <td>{p.date || '—'}</td>
                <td>{normalizeTenderMethod(p.method)}</td>
                <td><PaymentBadge status={p.status} /></td>
              </tr>
            )) : (
              <EmptyRow cols={8} message={loading ? 'Loading...' : emptyMessage} />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CoaAccountsTable({
  rows,
  sourceRows,
  loading,
  emptyMessage,
  filters,
  openKey,
  toggleFilter,
  updateFilter,
  onOpenLedger,
  onEdit,
  onDelete,
  showSideAndTotals = true,
  methodColumnLabel = 'Cash / Card',
}) {
  const colCount = showSideAndTotals ? 10 : 6;
  return (
    <div className="owner-table-wrap">
      <table className="owner-table rec-member-table admin-list-table admin-finance-table">
        <thead>
          <tr>
            <ColumnFilterHeader
              label="Account Name"
              filterKey="name"
              openKey={openKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
                placeholder="Search account..."
              />
            <ColumnFilterHeader
              label={methodColumnLabel}
              filterKey="method"
              openKey={openKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
                placeholder="Search method..."
              />
            {showSideAndTotals && (
              <ColumnFilterHeader
                label="Debit / Credit"
                filterKey="side"
                openKey={openKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search side..."
              />
            )}
            <ColumnFilterHeader
              label="Amount"
              filterKey="amount"
              openKey={openKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
                placeholder="Search amount..."
              />
            <ColumnFilterHeader
              label="Description"
              filterKey="description"
              openKey={openKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
                placeholder="Search description..."
              />
            <ColumnFilterHeader
              label="Entries"
              filterKey="entries"
              openKey={openKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
                placeholder="Search entries..."
              />
            {showSideAndTotals && (
              <>
                <ColumnFilterHeader
                  label="Total Debit"
                  filterKey="totalDebit"
                  openKey={openKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                placeholder="Search amount..."
              />
                <ColumnFilterHeader
                  label="Total Credit"
                  filterKey="totalCredit"
                  openKey={openKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                placeholder="Search amount..."
              />
                <ColumnFilterHeader
                  label="Balance"
                  filterKey="balance"
                  openKey={openKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                placeholder="Search amount..."
              />
              </>
            )}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((account) => {
            const { side, displayAmount, totalDebit, totalCredit } = account;
            const sideLabel = side === 'cash' ? '—' : side;
            const amountClass = side === 'credit' ? 'finance-amount-positive' : 'finance-amount-negative';
            return (
              <tr
                key={account.code}
                className="finance-account-row-clickable"
                onClick={() => onOpenLedger(account)}
              >
                <td>{account.name}</td>
                <td>{methodColumnLabel}</td>
                {showSideAndTotals && (
                  <td style={{ textTransform: 'capitalize' }}>{sideLabel}</td>
                )}
                <td className={amountClass}>{formatCurrency(displayAmount)}</td>
                <td>{account.description || '—'}</td>
                <td>{account.entryCount}</td>
                {showSideAndTotals && (
                  <>
                    <td className="finance-amount-negative">{formatCurrency(totalDebit)}</td>
                    <td className="finance-amount-positive">{formatCurrency(totalCredit)}</td>
                    <td className={amountClass}>{formatCurrency(account.balance)}</td>
                  </>
                )}
                <td>
                  {account.isCustom ? (
                    <div className="rec-member-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="owner-btn owner-btn-secondary owner-btn-sm"
                        onClick={() => onEdit(account)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="owner-btn owner-btn-sm admin-finance-btn-danger"
                        onClick={() => onDelete(account)}
                      >
                        Delete
                      </button>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          }) : <EmptyRow cols={colCount} message={loading ? 'Loading...' : emptyMessage} />}
        </tbody>
      </table>
    </div>
  );
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1A1F2B',
      borderColor: 'rgba(148, 163, 184, 0.14)',
      borderWidth: 1,
      titleColor: '#f1f5f9',
      bodyColor: TEXT_SECONDARY,
    },
  },
  scales: {
    x: { ticks: { color: TEXT_SECONDARY }, grid: { color: GRID_COLOR } },
    y: {
      ticks: {
        color: TEXT_SECONDARY,
        callback: (value) => formatCurrency(value),
      },
      grid: { color: GRID_COLOR },
    },
  },
};

const emptyData = {
  membershipPayments: [],
  payments: [],
  trainerSalaries: [],
  expenses: [],
  outstandingPayments: [],
  refunds: [],
  paymentHistory: [],
  guestPassIncome: [],
  monthlyMembershipLedger: [],
  categoryLedgers: [],
  memberLedgers: [],
  branchOptions: [],
  chartOfAccounts: [],
  expenseCategories: EXPENSE_CATEGORIES,
  revenueSummary: { monthly: 0, expenses: 0, profit: 0, outstanding: 0, collected: 0 },
  profitReport: {
    revenue: 0,
    membershipIncome: 0,
    guestPassIncome: 0,
    otherIncome: 0,
    trainerSalaries: 0,
    expenses: 0,
    refunds: 0,
    netProfit: 0,
  },
  revenueChartData: { labels: [], values: [] },
};

function AccountLedgerModal({ account, onClose }) {
  const entries = useMemo(
    () => [...(account.entries || [])].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [account.entries],
  );

  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    date: '',
    description: '',
    gymId: '',
    debit: '',
    credit: '',
    ref: '',
  });

  const filteredEntries = useMemo(
    () => entries.filter((entry) => (
      matchSaleHistoryDateFilter(entry.date, filters.date)
      && matchTextFilter(entry.description, filters.description)
      && matchGymIdFilter(entry.gymId, filters.gymId)
      && matchAmountFilter(entry.debit || 0, filters.debit, formatCurrency)
      && matchAmountFilter(entry.credit || 0, filters.credit, formatCurrency)
      && matchTextFilter(entry.ref, filters.ref)
    )),
    [entries, filters],
  );

  const summary = useMemo(() => {
    const totalDebit = entries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const totalCredit = entries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);
    const side = account.side || (account.type === 'expense' ? 'debit' : 'credit');
    return {
      totalDebit,
      totalCredit,
      balance: Number(account.balance) || 0,
      side,
      count: entries.length,
    };
  }, [entries, account]);

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div
        className="subscription-modal admin-form-modal finance-account-ledger-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-ledger-title"
      >
        <div className="subscription-modal-header">
          <h2 id="account-ledger-title" className="subscription-modal-title">Account Details</h2>
          <button type="button" className="subscription-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="subscription-form finance-account-ledger-form">
          <p className="finance-account-ledger-subtitle">
            Ledger for <strong>{account.name}</strong>
            {account.code && <span> ({account.code})</span>}
          </p>

          <div className="finance-account-ledger-meta">
            <span><strong>Type:</strong> {account.type || '—'}</span>
            <span><strong>Side:</strong> <span style={{ textTransform: 'capitalize' }}>{summary.side}</span></span>
            <span><strong>Description:</strong> {account.description || '—'}</span>
          </div>

          <div className="finance-account-ledger-stats">
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Total Debit</span>
              <strong className="finance-account-ledger-stat-value finance-amount-negative">
                {formatCurrency(summary.totalDebit)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Total Credit</span>
              <strong className="finance-account-ledger-stat-value finance-amount-positive">
                {formatCurrency(summary.totalCredit)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Balance</span>
              <strong className={`finance-account-ledger-stat-value ${summary.side === 'debit' ? 'finance-amount-negative' : 'finance-amount-positive'}`}>
                {formatCurrency(summary.balance)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Entries</span>
              <strong className="finance-account-ledger-stat-value">{summary.count}</strong>
            </div>
          </div>

          <div className="owner-table-wrap finance-account-ledger-table-wrap">
            <table className="owner-table finance-account-ledger-table admin-list-table">
              <thead>
                <tr>
                  <ColumnFilterHeader label="Date" filterKey="date" openKey={openFilterKey} onToggle={toggleFilter} filters={filters} onFilterChange={updateFilter} placeholder="DD-MM-YYYY" formatValue={formatDateFilterValue} />
                  <ColumnFilterHeader label="Description" filterKey="description" openKey={openFilterKey} onToggle={toggleFilter} filters={filters} onFilterChange={updateFilter} placeholder="Search description..." />
                  <ColumnFilterHeader label="Serial No" filterKey="gymId" openKey={openFilterKey} onToggle={toggleFilter} filters={filters} onFilterChange={updateFilter} placeholder="Search serial..." />
                  <ColumnFilterHeader label="Debit" filterKey="debit" openKey={openFilterKey} onToggle={toggleFilter} filters={filters} onFilterChange={updateFilter} placeholder="Search amount..." />
                  <ColumnFilterHeader label="Credit" filterKey="credit" openKey={openFilterKey} onToggle={toggleFilter} filters={filters} onFilterChange={updateFilter} placeholder="Search amount..." />
                  <ColumnFilterHeader label="Ref" filterKey="ref" openKey={openFilterKey} onToggle={toggleFilter} filters={filters} onFilterChange={updateFilter} placeholder="Search ref..." />
                </tr>
              </thead>
              <tbody>
                {filteredEntries.length > 0 ? (
                  filteredEntries.map((entry) => (
                    <tr key={`${entry.id}-${entry.date}-${entry.description}-${entry.ref}`}>
                      <td>{entry.date || '—'}</td>
                      <td>{entry.description || '—'}</td>
                      <td className="rec-member-gym-id">{entry.gymId || '—'}</td>
                      <td className="finance-amount-negative">
                        {entry.debit ? formatCurrency(entry.debit) : '—'}
                      </td>
                      <td className="finance-amount-positive">
                        {entry.credit ? formatCurrency(entry.credit) : '—'}
                      </td>
                      <td>{entry.ref || '—'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '20px' }}>
                      No entries in this account yet.
                    </td>
                  </tr>
                )}
              </tbody>
              {entries.length > 0 && (
                <tfoot>
                  <tr className="finance-account-ledger-totals-row">
                    <td colSpan={3}><strong>Totals</strong></td>
                    <td className="finance-amount-negative"><strong>{formatCurrency(summary.totalDebit)}</strong></td>
                    <td className="finance-amount-positive"><strong>{formatCurrency(summary.totalCredit)}</strong></td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="subscription-form-actions">
            <button type="button" className="owner-btn owner-btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MemberPnLLedgerModal({ member, onClose }) {
  const pnl = member.profitLoss || {};
  const entries = useMemo(
    () => [...(member.ledgerEntries || [])].sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [member.ledgerEntries],
  );
  const memberships = member.memberships || [];

  const {
    filters: membershipFilters,
    updateFilter: updateMembershipFilter,
    openFilterKey: membershipOpenKey,
    toggleFilter: toggleMembershipFilter,
  } = useColumnFilters({
    package: '',
    start: '',
    expiry: '',
    status: '',
  });

  const {
    filters: ledgerFilters,
    updateFilter: updateLedgerFilter,
    openFilterKey: ledgerOpenKey,
    toggleFilter: toggleLedgerFilter,
  } = useColumnFilters({
    date: '',
    type: '',
    description: '',
    debit: '',
    credit: '',
    balance: '',
    status: '',
  });

  const filteredMemberships = useMemo(
    () => memberships.filter((m) => (
      matchTextFilter(m.package, membershipFilters.package)
      && matchSaleHistoryDateFilter(m.start || m.startDate, membershipFilters.start)
      && matchSaleHistoryDateFilter(m.expiry || m.expiryDate, membershipFilters.expiry)
      && matchTextFilter(m.frozen ? 'frozen' : m.status, membershipFilters.status)
    )),
    [memberships, membershipFilters],
  );

  const filteredEntries = useMemo(
    () => entries.filter((entry) => (
      matchSaleHistoryDateFilter(entry.date, ledgerFilters.date)
      && matchTextFilter(entry.type, ledgerFilters.type)
      && matchTextFilter(entry.description, ledgerFilters.description)
      && matchAmountFilter(entry.debit || 0, ledgerFilters.debit, formatCurrency)
      && matchAmountFilter(entry.credit || 0, ledgerFilters.credit, formatCurrency)
      && matchAmountFilter(entry.balance || 0, ledgerFilters.balance, formatCurrency)
      && matchTextFilter(entry.status, ledgerFilters.status)
    )),
    [entries, ledgerFilters],
  );

  const totals = useMemo(() => {
    const totalDebit = entries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const totalCredit = entries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);
    return { totalDebit, totalCredit, count: entries.length };
  }, [entries]);

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div
        className="subscription-modal admin-form-modal finance-account-ledger-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="member-pnl-ledger-title"
      >
        <div className="subscription-modal-header">
          <h2 id="member-pnl-ledger-title" className="subscription-modal-title">Member Ledger</h2>
          <button type="button" className="subscription-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="subscription-form finance-account-ledger-form">
          <p className="finance-account-ledger-subtitle">
            Ledger for <strong>{member.name}</strong>
            {member.gymId && member.gymId !== '—' && (
              <span> ({member.gymId})</span>
            )}
          </p>

          <div className="finance-account-ledger-meta">
            <span><strong>Branch:</strong> {member.branch || '—'}</span>
            <span><strong>Phone:</strong> {member.phone || '—'}</span>
            <span><strong>Email:</strong> {member.email || '—'}</span>
            <span><strong>Joined:</strong> {member.joiningDate || '—'}</span>
          </div>

          <div className="finance-account-ledger-stats">
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Revenue</span>
              <strong className="finance-account-ledger-stat-value finance-amount-positive">
                {formatCurrency(pnl.revenue || 0)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Refunds</span>
              <strong className="finance-account-ledger-stat-value finance-amount-negative">
                {formatCurrency(pnl.refunds || 0)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Outstanding</span>
              <strong className="finance-account-ledger-stat-value">
                {formatCurrency(pnl.outstanding || 0)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Net Contribution</span>
              <strong className="finance-account-ledger-stat-value finance-amount-positive">
                {formatCurrency(pnl.netContribution || 0)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Total Debit</span>
              <strong className="finance-account-ledger-stat-value finance-amount-negative">
                {formatCurrency(totals.totalDebit)}
              </strong>
            </div>
            <div className="finance-account-ledger-stat">
              <span className="finance-account-ledger-stat-label">Total Credit</span>
              <strong className="finance-account-ledger-stat-value finance-amount-positive">
                {formatCurrency(totals.totalCredit)}
              </strong>
            </div>
          </div>

          <h3 className="finance-member-ledger-section-title">Memberships Taken</h3>
          <div className="owner-table-wrap finance-account-ledger-table-wrap">
            <table className="owner-table finance-account-ledger-table admin-list-table">
              <thead>
                <tr>
                  <ColumnFilterHeader label="Package" filterKey="package" openKey={membershipOpenKey} onToggle={toggleMembershipFilter} filters={membershipFilters} onFilterChange={updateMembershipFilter} placeholder="Search package..." />
                  <ColumnFilterHeader label="Start" filterKey="start" openKey={membershipOpenKey} onToggle={toggleMembershipFilter} filters={membershipFilters} onFilterChange={updateMembershipFilter} placeholder="DD-MM-YYYY" formatValue={formatDateFilterValue} />
                  <ColumnFilterHeader label="Expiry" filterKey="expiry" openKey={membershipOpenKey} onToggle={toggleMembershipFilter} filters={membershipFilters} onFilterChange={updateMembershipFilter} placeholder="DD-MM-YYYY" formatValue={formatDateFilterValue} />
                  <ColumnFilterHeader label="Status" filterKey="status" openKey={membershipOpenKey} onToggle={toggleMembershipFilter} filters={membershipFilters} onFilterChange={updateMembershipFilter} placeholder="Search status..." />
                </tr>
              </thead>
              <tbody>
                {filteredMemberships.length > 0 ? (
                  filteredMemberships.map((m) => (
                    <tr key={m.id}>
                      <td>{m.package || '—'}</td>
                      <td>{m.start || m.startDate || '—'}</td>
                      <td>{m.expiry || m.expiryDate || '—'}</td>
                      <td><PaymentBadge status={m.frozen ? 'frozen' : m.status} /></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '16px' }}>
                      No memberships on record.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h3 className="finance-member-ledger-section-title">Ledger Entries</h3>
          <div className="owner-table-wrap finance-account-ledger-table-wrap">
            <table className="owner-table finance-account-ledger-table admin-list-table">
              <thead>
                <tr>
                  <ColumnFilterHeader label="Date" filterKey="date" openKey={ledgerOpenKey} onToggle={toggleLedgerFilter} filters={ledgerFilters} onFilterChange={updateLedgerFilter} placeholder="DD-MM-YYYY" formatValue={formatDateFilterValue} />
                  <ColumnFilterHeader label="Type" filterKey="type" openKey={ledgerOpenKey} onToggle={toggleLedgerFilter} filters={ledgerFilters} onFilterChange={updateLedgerFilter} placeholder="Search type..." />
                  <ColumnFilterHeader label="Description" filterKey="description" openKey={ledgerOpenKey} onToggle={toggleLedgerFilter} filters={ledgerFilters} onFilterChange={updateLedgerFilter} placeholder="Search description..." />
                  <ColumnFilterHeader label="Debit" filterKey="debit" openKey={ledgerOpenKey} onToggle={toggleLedgerFilter} filters={ledgerFilters} onFilterChange={updateLedgerFilter} placeholder="Search amount..." />
                  <ColumnFilterHeader label="Credit" filterKey="credit" openKey={ledgerOpenKey} onToggle={toggleLedgerFilter} filters={ledgerFilters} onFilterChange={updateLedgerFilter} placeholder="Search amount..." />
                  <ColumnFilterHeader label="Balance" filterKey="balance" openKey={ledgerOpenKey} onToggle={toggleLedgerFilter} filters={ledgerFilters} onFilterChange={updateLedgerFilter} placeholder="Search amount..." />
                  <ColumnFilterHeader label="Status" filterKey="status" openKey={ledgerOpenKey} onToggle={toggleLedgerFilter} filters={ledgerFilters} onFilterChange={updateLedgerFilter} placeholder="Search status..." />
                </tr>
              </thead>
              <tbody>
                {filteredEntries.length > 0 ? (
                  filteredEntries.map((entry) => (
                    <tr key={`${entry.id}-${entry.type}-${entry.date}`}>
                      <td>{entry.date || '—'}</td>
                      <td>{entry.type || '—'}</td>
                      <td>{entry.description || '—'}</td>
                      <td className="finance-amount-negative">
                        {entry.debit ? formatCurrency(entry.debit) : '—'}
                      </td>
                      <td className="finance-amount-positive">
                        {entry.credit ? formatCurrency(entry.credit) : '—'}
                      </td>
                      <td>{formatCurrency(entry.balance || 0)}</td>
                      <td><PaymentBadge status={entry.status} /></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '16px' }}>
                      No ledger entries for this member.
                    </td>
                  </tr>
                )}
              </tbody>
              {entries.length > 0 && (
                <tfoot>
                  <tr className="finance-account-ledger-totals-row">
                    <td colSpan={3}><strong>Totals</strong></td>
                    <td className="finance-amount-negative"><strong>{formatCurrency(totals.totalDebit)}</strong></td>
                    <td className="finance-amount-positive"><strong>{formatCurrency(totals.totalCredit)}</strong></td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="subscription-form-actions">
            <button type="button" className="owner-btn owner-btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FinanceManagement() {
  const toast = usePanelToast();
  const { account } = useAuth();
  const gymId = account?.gym_id || '';
  const cachedLedger = peekGymCache('finance-ledger:v5');
  const [tab, setTab] = useState('accounts');

  useEffect(() => {
    if (!TABS.some((item) => item.id === tab)) {
      setTab('accounts');
    }
  }, [tab]);
  const [zReportDate, setZReportDate] = useState(() => localDateKey());
  const [journalQuery, setJournalQuery] = useState({
    memberOrGym: '',
    type: 'all', // all | cash | bank | card | qr
    fromDate: '',
    toDate: '',
  });
  const [showExpenseLedger, setShowExpenseLedger] = useState(false);
  const [expenseLedgerCategory, setExpenseLedgerCategory] = useState('all');
  const [data, setData] = useState(cachedLedger || emptyData);
  const [loading, setLoading] = useState(!cachedLedger);
  const [error, setError] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [memberLedgerTarget, setMemberLedgerTarget] = useState(null);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingConfirm, setDeletingConfirm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccountCode, setEditingAccountCode] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const accountFormRef = useRef(null);
  const [accountForm, setAccountForm] = useState({
    name: '',
    method: 'cash',
    side: 'debit',
    amount: '',
    code: '',
    description: '',
  });
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    category: 'Other',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    branch: '',
    status: 'paid',
  });
  const [showCashPaymentForm, setShowCashPaymentForm] = useState(false);
  const [savingCashPayment, setSavingCashPayment] = useState(false);
  const [cashPaymentForm, setCashPaymentForm] = useState({
    code: '',
    accountTitle: '',
    description: '',
    payment: '',
    date: localDateKey(),
  });
  const [manualCashPayments, setManualCashPayments] = useState([]);
  const [showBankPaymentForm, setShowBankPaymentForm] = useState(false);
  const [savingBankPayment, setSavingBankPayment] = useState(false);
  const [bankPaymentForm, setBankPaymentForm] = useState({
    code: '',
    accountTitle: '',
    description: '',
    credit: '',
    debit: '',
    date: localDateKey(),
  });
  const [manualBankPayments, setManualBankPayments] = useState([]);
  const [showCardPaymentForm, setShowCardPaymentForm] = useState(false);
  const [savingCardPayment, setSavingCardPayment] = useState(false);
  const [cardPaymentForm, setCardPaymentForm] = useState({
    code: '',
    accountTitle: '',
    description: '',
    credit: '',
    debit: '',
    date: localDateKey(),
  });
  const [manualCardPayments, setManualCardPayments] = useState([]);
  const [manualQrPayments, setManualQrPayments] = useState([]);
  const [showManualExpenseForm, setShowManualExpenseForm] = useState(false);
  const [showExpenseCategoryForm, setShowExpenseCategoryForm] = useState(false);
  const [savingManualExpense, setSavingManualExpense] = useState(false);
  const [savingExpenseCategory, setSavingExpenseCategory] = useState(false);
  const [expenseCategoryName, setExpenseCategoryName] = useState('');
  const [manualExpenseForm, setManualExpenseForm] = useState({
    code: '',
    accountTitle: '',
    description: '',
    category: 'Other',
    branch: '',
    amount: '',
    date: localDateKey(),
    method: 'cash',
  });
  const [manualExpenses, setManualExpenses] = useState([]);
  const [coaZExpenses, setCoaZExpenses] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);

  const subscriptionAccountRows = useMemo(
    () => buildSubscriptionAccountRows(financePaymentPool(data)),
    [data.payments, data.guestPassIncome],
  );

  const allCashPayments = useMemo(
    () => [...(subscriptionAccountRows.cash || []), ...(manualCashPayments || [])]
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [subscriptionAccountRows.cash, manualCashPayments],
  );

  const allBankPayments = useMemo(
    () => [...(subscriptionAccountRows.bank || []), ...(manualBankPayments || [])]
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [subscriptionAccountRows.bank, manualBankPayments],
  );

  const allCardPayments = useMemo(
    () => [...(subscriptionAccountRows.card || []), ...(manualCardPayments || [])]
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [subscriptionAccountRows.card, manualCardPayments],
  );

  const allQrPayments = useMemo(
    () => [...(subscriptionAccountRows.qr || []), ...(manualQrPayments || [])]
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
    [subscriptionAccountRows.qr, manualQrPayments],
  );

  const accountRows = useMemo(() => {
    const cashEntries = (allCashPayments || []).map((p) => ({
      id: p.id,
      date: p.date,
      description: [p.accountTitle, p.description].filter(Boolean).join(' — ') || 'Cash payment',
      gymId: '—',
      debit: Number(p.payment) || 0,
      credit: 0,
      ref: p.code || '—',
      method: 'Cash',
    }));
    const cashDebit = cashEntries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const cashCredit = cashEntries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);

    const bankEntries = (allBankPayments || []).map((p) => ({
      id: p.id,
      date: p.date,
      description: [p.accountTitle, p.description].filter(Boolean).join(' — ') || 'Bank payment',
      gymId: '—',
      debit: Number(p.debit) || 0,
      credit: Number(p.credit) || 0,
      ref: p.code || '—',
      method: 'Bank',
    }));
    const bankDebit = bankEntries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const bankCredit = bankEntries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);

    const cardEntries = (allCardPayments || []).map((p) => ({
      id: p.id,
      date: p.date,
      description: [p.accountTitle, p.description].filter(Boolean).join(' — ') || 'Card payment',
      gymId: '—',
      debit: Number(p.debit) || 0,
      credit: Number(p.credit) || 0,
      ref: p.code || '—',
      method: 'Card',
    }));
    const cardDebit = cardEntries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const cardCredit = cardEntries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);

    const qrEntries = (allQrPayments || []).map((p) => ({
      id: p.id,
      date: p.date,
      description: [p.accountTitle, p.description].filter(Boolean).join(' — ') || 'QR payment',
      gymId: '—',
      debit: Number(p.debit) || 0,
      credit: Number(p.credit) || 0,
      ref: p.code || '—',
      method: 'QR',
    }));
    const qrDebit = qrEntries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const qrCredit = qrEntries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);

    const expenseEntries = (manualExpenses || []).map((p) => ({
      id: p.id,
      date: p.date,
      description: [p.accountTitle, p.description].filter(Boolean).join(' — ') || 'Expense',
      gymId: '—',
      debit: Number(p.amount) || 0,
      credit: 0,
      ref: p.code || '—',
      method: String(p.method || p.paymentMethod || 'cash').toLowerCase() === 'bank' ? 'Bank' : 'Cash',
    }));
    const expenseDebit = expenseEntries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const expenseCredit = expenseEntries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);

    return [
      {
        code: 'CASH',
        name: 'Cash',
        type: 'asset',
        side: 'debit',
        method: 'cash',
        ledgerGroup: 'cash',
        description: 'From Cash Accounts + subscription cash payments',
        entryCount: cashEntries.length,
        totalDebit: cashDebit,
        totalCredit: cashCredit,
        balance: cashDebit - cashCredit,
        displayAmount: cashDebit - cashCredit,
        amount: cashDebit - cashCredit,
        entries: cashEntries,
        isCustom: false,
      },
      {
        code: 'BANK',
        name: 'Bank',
        type: 'asset',
        side: 'debit',
        method: 'bank',
        ledgerGroup: 'bank',
        description: 'From Bank Accounts + online / bank subscription payments',
        entryCount: bankEntries.length,
        totalDebit: bankDebit,
        totalCredit: bankCredit,
        balance: bankDebit - bankCredit,
        displayAmount: bankDebit - bankCredit,
        amount: bankDebit - bankCredit,
        entries: bankEntries,
        isCustom: false,
      },
      {
        code: 'CARD',
        name: 'Card',
        type: 'asset',
        side: 'debit',
        method: 'card',
        ledgerGroup: 'card',
        description: 'From Card Payment + subscription card payments',
        entryCount: cardEntries.length,
        totalDebit: cardDebit,
        totalCredit: cardCredit,
        balance: cardDebit - cardCredit,
        displayAmount: cardDebit - cardCredit,
        amount: cardDebit - cardCredit,
        entries: cardEntries,
        isCustom: false,
      },
      {
        code: 'QR',
        name: 'QR',
        type: 'asset',
        side: 'debit',
        method: 'qr',
        ledgerGroup: 'qr',
        description: 'From QR Payment + subscription QR payments',
        entryCount: qrEntries.length,
        totalDebit: qrDebit,
        totalCredit: qrCredit,
        balance: qrDebit - qrCredit,
        displayAmount: qrDebit - qrCredit,
        amount: qrDebit - qrCredit,
        entries: qrEntries,
        isCustom: false,
      },
      {
        code: 'EXPENSE',
        name: 'Expense',
        type: 'expense',
        side: 'debit',
        method: 'expense',
        ledgerGroup: 'expense',
        description: 'From Expense entries',
        entryCount: expenseEntries.length,
        totalDebit: expenseDebit,
        totalCredit: expenseCredit,
        balance: expenseDebit - expenseCredit,
        displayAmount: expenseDebit - expenseCredit,
        amount: expenseDebit - expenseCredit,
        entries: expenseEntries,
        isCustom: false,
      },
    ];
  }, [allCashPayments, allBankPayments, allCardPayments, allQrPayments, manualExpenses]);

  const {
    filters: paymentFilters,
    updateFilter: updatePaymentFilter,
    openFilterKey: paymentOpenKey,
    toggleFilter: togglePaymentFilter,
  } = useColumnFilters({
    id: '',
    gymId: '',
    member: '',
    package: '',
    amount: '',
    date: '',
    method: '',
    status: '',
  });

  const {
    filters: outstandingFilters,
    updateFilter: updateOutstandingFilter,
    openFilterKey: outstandingOpenKey,
    toggleFilter: toggleOutstandingFilter,
  } = useColumnFilters({
    id: '',
    gymId: '',
    member: '',
    package: '',
    amount: '',
    dueDate: '',
    daysOverdue: '',
    status: '',
  });

  const {
    filters: historyFilters,
    updateFilter: updateHistoryFilter,
    openFilterKey: historyOpenKey,
    toggleFilter: toggleHistoryFilter,
  } = useColumnFilters({
    id: '',
    gymId: '',
    member: '',
    type: '',
    amount: '',
    date: '',
    method: '',
    status: '',
  });

  const {
    filters: cashPaymentFilters,
    updateFilter: updateCashPaymentFilter,
    openFilterKey: cashPaymentOpenKey,
    toggleFilter: toggleCashPaymentFilter,
  } = useColumnFilters({
    id: '',
    gymId: '',
    member: '',
    type: '',
    amount: '',
    date: '',
    method: '',
    status: '',
    code: '',
    accountTitle: '',
    description: '',
    payment: '',
  });

  const {
    filters: bankPaymentFilters,
    updateFilter: updateBankPaymentFilter,
    openFilterKey: bankPaymentOpenKey,
    toggleFilter: toggleBankPaymentFilter,
  } = useColumnFilters({
    id: '',
    gymId: '',
    member: '',
    type: '',
    amount: '',
    date: '',
    method: '',
    status: '',
    code: '',
    accountTitle: '',
    description: '',
    credit: '',
    debit: '',
  });

  const {
    filters: cardPaymentFilters,
    updateFilter: updateCardPaymentFilter,
    openFilterKey: cardPaymentOpenKey,
    toggleFilter: toggleCardPaymentFilter,
  } = useColumnFilters({
    code: '',
    gymId: '',
    accountTitle: '',
    description: '',
    credit: '',
    debit: '',
    date: '',
  });

  const {
    filters: qrPaymentFilters,
    updateFilter: updateQrPaymentFilter,
    openFilterKey: qrPaymentOpenKey,
    toggleFilter: toggleQrPaymentFilter,
  } = useColumnFilters({
    code: '',
    gymId: '',
    accountTitle: '',
    description: '',
    credit: '',
    debit: '',
    date: '',
  });

  const {
    filters: manualExpenseFilters,
    updateFilter: updateManualExpenseFilter,
    openFilterKey: manualExpenseOpenKey,
    toggleFilter: toggleManualExpenseFilter,
  } = useColumnFilters({
    code: '',
    accountTitle: '',
    description: '',
    category: '',
    branch: '',
    amount: '',
    date: '',
  });

  const {
    filters: salesEntryFilters,
    updateFilter: updateSalesEntryFilter,
    openFilterKey: salesEntryOpenKey,
    toggleFilter: toggleSalesEntryFilter,
  } = useColumnFilters({
    code: '',
    gymId: '',
    member: '',
    category: '',
    item: '',
    method: '',
    amount: '',
    date: '',
  });

  const {
    filters: memberFilters,
    updateFilter: updateMemberFilter,
    openFilterKey: memberOpenKey,
    toggleFilter: toggleMemberFilter,
  } = useColumnFilters({
    gymId: '',
    name: '',
    branch: '',
    status: '',
    netPnl: '',
  });

  const {
    filters: expenseFilters,
    updateFilter: updateExpenseFilter,
    openFilterKey: expenseOpenKey,
    toggleFilter: toggleExpenseFilter,
  } = useColumnFilters({
    description: '',
    category: '',
    branch: '',
    amount: '',
    date: '',
    status: '',
  });

  const {
    filters: accountFilters,
    updateFilter: updateAccountFilter,
    openFilterKey: accountOpenKey,
    toggleFilter: toggleAccountFilter,
  } = useColumnFilters({
    name: '',
    method: '',
    side: '',
    amount: '',
    description: '',
    entries: '',
    totalDebit: '',
    totalCredit: '',
    balance: '',
  });

  const {
    filters: journalFilters,
    updateFilter: updateJournalFilter,
    openFilterKey: journalOpenKey,
    toggleFilter: toggleJournalFilter,
  } = useColumnFilters({
    date: '',
    account: '',
    description: '',
    gymId: '',
    method: '',
    debit: '',
    credit: '',
    ref: '',
    category: '',
  });

  const {
    filters: expenseLedgerFilters,
    updateFilter: updateExpenseLedgerFilter,
    openFilterKey: expenseLedgerOpenKey,
    toggleFilter: toggleExpenseLedgerFilter,
  } = useColumnFilters({
    date: '',
    code: '',
    account: '',
    category: '',
    description: '',
    amount: '',
  });

  const {
    filters: monthlyFilters,
    updateFilter: updateMonthlyFilter,
    openFilterKey: monthlyOpenKey,
    toggleFilter: toggleMonthlyFilter,
  } = useColumnFilters({
    label: '',
    paymentCount: '',
    newMemberships: '',
    revenue: '',
    packages: '',
  });

  const {
    filters: categoryEntryFilters,
    updateFilter: updateCategoryEntryFilter,
    openFilterKey: categoryEntryOpenKey,
    toggleFilter: toggleCategoryEntryFilter,
  } = useColumnFilters({
    date: '',
    description: '',
    gymId: '',
    debit: '',
    credit: '',
    ref: '',
  });

  const {
    filters: pnlMemberFilters,
    updateFilter: updatePnlMemberFilter,
    openFilterKey: pnlMemberOpenKey,
    toggleFilter: togglePnlMemberFilter,
  } = useColumnFilters({
    gymId: '',
    name: '',
    revenue: '',
    refunds: '',
    outstanding: '',
    netContribution: '',
  });

  const {
    filters: zPackageFilters,
    updateFilter: updateZPackageFilter,
    openFilterKey: zPackageOpenKey,
    toggleFilter: toggleZPackageFilter,
  } = useColumnFilters({
    package: '',
    count: '',
    credit: '',
    debit: '',
  });

  const {
    filters: zMethodFilters,
    updateFilter: updateZMethodFilter,
    openFilterKey: zMethodOpenKey,
    toggleFilter: toggleZMethodFilter,
  } = useColumnFilters({
    method: '',
    count: '',
    amount: '',
  });

  const {
    filters: zExpenseFilters,
    updateFilter: updateZExpenseFilter,
    openFilterKey: zExpenseOpenKey,
    toggleFilter: toggleZExpenseFilter,
  } = useColumnFilters({
    description: '',
    category: '',
    amount: '',
  });

  const {
    filters: memberMembershipFilters,
    updateFilter: updateMemberMembershipFilter,
    openFilterKey: memberMembershipOpenKey,
    toggleFilter: toggleMemberMembershipFilter,
  } = useColumnFilters({
    package: '',
    start: '',
    expiry: '',
    status: '',
  });

  const {
    filters: memberLedgerFilters,
    updateFilter: updateMemberLedgerFilter,
    openFilterKey: memberLedgerOpenKey,
    toggleFilter: toggleMemberLedgerFilter,
  } = useColumnFilters({
    date: '',
    type: '',
    description: '',
    debit: '',
    credit: '',
    balance: '',
    status: '',
  });

  const loadData = useCallback(async () => {
    if (!gymId) {
      setLoading(false);
      setError('No gym is linked to this account. Sign out and sign in again.');
      setData(emptyData);
      return;
    }

    try {
      setError('');
      if (!peekGymCache('finance-ledger:v5')) setLoading(true);
      setActiveGymId(gymId);
      const ledger = await fetchFinanceLedgerData(gymId);
      setData(ledger);
      setManualCashPayments(loadManualCashPayments(gymId));
      setManualBankPayments(loadManualBankPayments(gymId));
      setManualCardPayments(loadManualCardPayments(gymId));
      setManualQrPayments(loadManualQrPayments(gymId));

      // Categories: prefer Supabase, keep localStorage as cache / offline fallback
      let categoryRecords = [];
      try {
        const remoteCats = await fetchExpenseCategories(gymId);
        if (remoteCats.length) {
          categoryRecords = syncExpenseCategoryRecords(gymId, remoteCats);
        } else {
          const localCats = loadExpenseCategoryRecords(gymId);
          if (localCats.length) {
            // One-time migrate local categories to cloud when table exists but is empty
            await Promise.allSettled(
              localCats.map((cat) => createExpenseCategoryRemote({
                name: cat.name,
                codeBase: cat.codeBase,
              }, gymId)),
            );
            const migrated = await fetchExpenseCategories(gymId);
            categoryRecords = migrated.length
              ? syncExpenseCategoryRecords(gymId, migrated)
              : localCats;
          }
        }
      } catch {
        categoryRecords = loadExpenseCategoryRecords(gymId);
      }
      setExpenseCategories(categoryRecords.map((r) => r.name));

      // Expenses: prefer Supabase so deploy / other browsers see the same rows
      const apiExpenses = (ledger.expenses || []).map(mapApiExpenseToManual);
      const localExpenses = loadManualExpenses(gymId);
      let merged = mergeManualExpenses(apiExpenses, localExpenses);

      // Migrate browser-only expenses to Supabase once
      const apiIds = new Set(apiExpenses.map((r) => String(r.id)));
      const toMigrate = localExpenses.filter((row) => {
        const id = String(row.id || '');
        return id.startsWith('EXP-') && !apiIds.has(id);
      });
      if (toMigrate.length) {
        const migrated = [];
        await Promise.allSettled(
          toMigrate.map(async (row) => {
            const created = await createExpense({
              accountTitle: row.accountTitle,
              description: row.description,
              category: row.category,
              branch: row.branch,
              amount: row.amount,
              date: row.date,
              code: row.code,
              status: 'paid',
            }, gymId);
            deleteManualExpense(gymId, row.id);
            migrated.push(mapApiExpenseToManual(created));
          }),
        );
        if (migrated.length) {
          merged = mergeManualExpenses(
            [...apiExpenses, ...migrated],
            loadManualExpenses(gymId),
          );
        }
      }

      if (apiExpenses.length || toMigrate.length) {
        // Cloud is source of truth once it has data — drop stale local cache
        const stillLocal = loadManualExpenses(gymId);
        if (!stillLocal.length) clearManualExpensesCache(gymId);
      }

      setManualExpenses(merged);
      setSelectedAccount((current) => current || 'CASH');
    } catch (err) {
      setError(err.message || 'Failed to load finance ledger');
      if (!peekGymCache('finance-ledger:v5')) setData(emptyData);
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh Z Report sources when the tab is opened or the report date changes
  useEffect(() => {
    if (tab !== 'zreport' || !gymId) return;
    loadData();
  }, [tab, zReportDate, gymId, loadData]);

  // Load ADM / SEL / OEXP ledger expenses (cash vs bank Type) for the Z Report date
  useEffect(() => {
    if (!gymId || !zReportDate) {
      setCoaZExpenses([]);
      return undefined;
    }
    let cancelled = false;
    fetchCoaZReportExpenses(zReportDate, gymId)
      .then((rows) => {
        if (cancelled) return;
        setCoaZExpenses((rows || []).map(mapApiExpenseToManual));
      })
      .catch(() => {
        if (!cancelled) setCoaZExpenses([]);
      });
    return () => { cancelled = true; };
  }, [gymId, zReportDate, tab]);

  useEffect(() => {
    const refresh = () => {
      invalidateCache(`gym:${gymId}:finance-ledger:v5`);
      loadData();
    };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [gymId, loadData]);

  useEffect(() => {
    if (!showAccountForm || !editingAccountCode) return undefined;
    const timer = window.setTimeout(() => {
      accountFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const nameInput = document.getElementById('coa-name');
      nameInput?.focus?.({ preventScroll: true });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [showAccountForm, editingAccountCode]);

  const journalMemberGymOptions = useMemo(() => {
    const values = new Set();
    const push = (v) => {
      const text = String(v || '').trim();
      if (text && text !== '—') values.add(text);
    };
    [...(allCashPayments || []), ...(allBankPayments || []), ...(allCardPayments || []), ...(allQrPayments || [])].forEach((p) => {
      push(p.gymId);
      push(p.description); // member name for subscription rows
    });
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [allCashPayments, allBankPayments, allCardPayments, allQrPayments]);

  const journalEntries = useMemo(() => {
    const type = journalQuery.type || 'all';
    const needle = String(journalQuery.memberOrGym || '').trim().toLowerCase();
    const fromDate = String(journalQuery.fromDate || '').trim().slice(0, 10);
    const toDate = String(journalQuery.toDate || '').trim().slice(0, 10);

    const cashRows = (type === 'all' || type === 'cash' ? (allCashPayments || []) : []).map((p) => ({
      id: `cash-${p.id}`,
      date: p.date || '—',
      account: p.accountTitle || '—',
      accountCode: p.code || '',
      description: p.description || '—',
      gymId: p.gymId || '—',
      method: 'Cash',
      debit: Number(p.payment) || 0,
      credit: 0,
      ref: p.code || '—',
      source: 'cash',
      category: '',
    }));

    const bankRows = (type === 'all' || type === 'bank' ? (allBankPayments || []) : []).map((p) => ({
      id: `bank-${p.id}`,
      date: p.date || '—',
      account: p.accountTitle || '—',
      accountCode: p.code || '',
      description: p.description || '—',
      gymId: p.gymId || '—',
      method: 'Bank',
      debit: Number(p.debit) || 0,
      credit: Number(p.credit) || 0,
      ref: p.code || '—',
      source: 'bank',
      category: '',
    }));

    const cardRows = (type === 'all' || type === 'card' ? (allCardPayments || []) : []).map((p) => ({
      id: `card-${p.id}`,
      date: p.date || '—',
      account: p.accountTitle || '—',
      accountCode: p.code || '',
      description: p.description || '—',
      gymId: p.gymId || '—',
      method: 'Card',
      debit: Number(p.debit) || 0,
      credit: Number(p.credit) || 0,
      ref: p.code || '—',
      source: 'card',
      category: '',
    }));

    const qrRows = (type === 'all' || type === 'qr' ? (allQrPayments || []) : []).map((p) => ({
      id: `qr-${p.id}`,
      date: p.date || '—',
      account: p.accountTitle || '—',
      accountCode: p.code || '',
      description: p.description || '—',
      gymId: p.gymId || '—',
      method: 'QR',
      debit: Number(p.debit) || 0,
      credit: Number(p.credit) || 0,
      ref: p.code || '—',
      source: 'qr',
      category: '',
    }));

    return [...cashRows, ...bankRows, ...cardRows, ...qrRows]
      .filter((row) => {
        if (needle) {
          const gym = String(row.gymId || '').toLowerCase();
          const member = String(row.description || '').toLowerCase();
          if (!gym.includes(needle) && !member.includes(needle)) return false;
        }
        const rowDate = String(row.date || '').slice(0, 10);
        if (fromDate && rowDate && rowDate !== '—' && rowDate < fromDate) return false;
        if (toDate && rowDate && rowDate !== '—' && rowDate > toDate) return false;
        return true;
      })
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [
    allCashPayments,
    allBankPayments,
    allCardPayments,
    allQrPayments,
    journalQuery.memberOrGym,
    journalQuery.type,
    journalQuery.fromDate,
    journalQuery.toDate,
  ]);

  const expenseLedgerEntries = useMemo(() => {
    const categoryFilter = String(expenseLedgerCategory || 'all').trim().toLowerCase();
    return (manualExpenses || [])
      .filter((p) => {
        if (categoryFilter === 'all') return true;
        return String(p.category || '').trim().toLowerCase() === categoryFilter;
      })
      .map((p) => ({
        id: `expense-${p.id}`,
        date: p.date || '—',
        account: p.accountTitle || '—',
        accountCode: p.code || '',
        description: p.description || '—',
        category: p.category || 'Other',
        method: 'Expense',
        debit: Number(p.amount) || 0,
        credit: 0,
        ref: p.code || '—',
        source: 'expense',
      }))
      .sort(compareExpenseCodes);
  }, [manualExpenses, expenseLedgerCategory]);

  const filteredMembershipPayments = useMemo(
    () => (data.membershipPayments || []).filter((p) => (
      matchTextFilter(p.id, paymentFilters.id)
      && matchGymIdFilter(p.gymId, paymentFilters.gymId)
      && matchTextFilter(p.member, paymentFilters.member)
      && matchTextFilter(p.package, paymentFilters.package)
      && matchAmountFilter(p.amount, paymentFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(p.date, paymentFilters.date)
      && matchTextFilter(p.method, paymentFilters.method)
      && matchTextFilter(p.status, paymentFilters.status)
    )),
    [data.membershipPayments, paymentFilters],
  );

  const filteredOutstanding = useMemo(
    () => (data.outstandingPayments || []).filter((p) => (
      matchTextFilter(p.id, outstandingFilters.id)
      && matchGymIdFilter(p.gymId, outstandingFilters.gymId)
      && matchTextFilter(p.member, outstandingFilters.member)
      && matchTextFilter(p.package, outstandingFilters.package)
      && matchAmountFilter(p.amount, outstandingFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(p.dueDate, outstandingFilters.dueDate)
      && matchTextFilter(p.daysOverdue > 0 ? `${p.daysOverdue} days` : '—', outstandingFilters.daysOverdue)
      && matchTextFilter(p.status, outstandingFilters.status)
    )),
    [data.outstandingPayments, outstandingFilters],
  );

  const filteredHistory = useMemo(
    () => (data.paymentHistory || []).filter((p) => (
      matchTextFilter(p.id, historyFilters.id)
      && matchGymIdFilter(p.gymId, historyFilters.gymId)
      && matchTextFilter(p.member, historyFilters.member)
      && matchTextFilter(p.type, historyFilters.type)
      && matchAmountFilter(p.amount, historyFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(p.date, historyFilters.date)
      && matchTextFilter(p.method, historyFilters.method)
      && matchTextFilter(p.status, historyFilters.status)
    )),
    [data.paymentHistory, historyFilters],
  );

  const cashPaymentSource = useMemo(
    () => (data.paymentHistory || []).filter((p) => normalizeTenderMethod(p.method) === 'Cash'),
    [data.paymentHistory],
  );

  const bankPaymentSource = useMemo(
    () => (data.paymentHistory || []).filter((p) => normalizeTenderMethod(p.method) !== 'Cash'),
    [data.paymentHistory],
  );

  const filteredCashPayments = useMemo(
    () => cashPaymentSource.filter((p) => (
      matchTextFilter(p.id, cashPaymentFilters.id)
      && matchGymIdFilter(p.gymId, cashPaymentFilters.gymId)
      && matchTextFilter(p.member, cashPaymentFilters.member)
      && matchTextFilter(p.type, cashPaymentFilters.type)
      && matchAmountFilter(p.amount, cashPaymentFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(p.date, cashPaymentFilters.date)
      && matchTextFilter(normalizeTenderMethod(p.method), cashPaymentFilters.method)
      && matchTextFilter(p.status, cashPaymentFilters.status)
    )),
    [cashPaymentSource, cashPaymentFilters],
  );

  const filteredBankPayments = useMemo(
    () => bankPaymentSource.filter((p) => (
      matchTextFilter(p.id, bankPaymentFilters.id)
      && matchGymIdFilter(p.gymId, bankPaymentFilters.gymId)
      && matchTextFilter(p.member, bankPaymentFilters.member)
      && matchTextFilter(p.type, bankPaymentFilters.type)
      && matchAmountFilter(p.amount, bankPaymentFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(p.date, bankPaymentFilters.date)
      && matchTextFilter(normalizeTenderMethod(p.method), bankPaymentFilters.method)
      && matchTextFilter(p.status, bankPaymentFilters.status)
    )),
    [bankPaymentSource, bankPaymentFilters],
  );

  const filteredManualCashPayments = useMemo(
    () => (allCashPayments || []).filter((row) => (
      matchTextFilter(row.code, cashPaymentFilters.code)
      && matchGymIdFilter(row.gymId, cashPaymentFilters.gymId)
      && matchTextFilter(row.accountTitle, cashPaymentFilters.accountTitle)
      && matchTextFilter(row.description, cashPaymentFilters.description)
      && matchAmountFilter(row.payment, cashPaymentFilters.payment, formatCurrency)
      && matchSaleHistoryDateFilter(row.date, cashPaymentFilters.date)
    )),
    [allCashPayments, cashPaymentFilters],
  );

  const filteredManualBankPayments = useMemo(
    () => (allBankPayments || []).filter((row) => (
      matchTextFilter(row.code, bankPaymentFilters.code)
      && matchGymIdFilter(row.gymId, bankPaymentFilters.gymId)
      && matchTextFilter(row.accountTitle, bankPaymentFilters.accountTitle)
      && matchTextFilter(row.description, bankPaymentFilters.description)
      && matchAmountFilter(row.credit || 0, bankPaymentFilters.credit, formatCurrency)
      && matchAmountFilter(row.debit || 0, bankPaymentFilters.debit, formatCurrency)
      && matchSaleHistoryDateFilter(row.date, bankPaymentFilters.date)
    )),
    [allBankPayments, bankPaymentFilters],
  );

  const filteredManualCardPayments = useMemo(
    () => (allCardPayments || []).filter((row) => (
      matchTextFilter(row.code, cardPaymentFilters.code)
      && matchGymIdFilter(row.gymId, cardPaymentFilters.gymId)
      && matchTextFilter(row.accountTitle, cardPaymentFilters.accountTitle)
      && matchTextFilter(row.description, cardPaymentFilters.description)
      && matchAmountFilter(row.credit || 0, cardPaymentFilters.credit, formatCurrency)
      && matchAmountFilter(row.debit || 0, cardPaymentFilters.debit, formatCurrency)
      && matchSaleHistoryDateFilter(row.date, cardPaymentFilters.date)
    )),
    [allCardPayments, cardPaymentFilters],
  );

  const filteredManualQrPayments = useMemo(
    () => (allQrPayments || []).filter((row) => (
      matchTextFilter(row.code, qrPaymentFilters.code)
      && matchGymIdFilter(row.gymId, qrPaymentFilters.gymId)
      && matchTextFilter(row.accountTitle, qrPaymentFilters.accountTitle)
      && matchTextFilter(row.description, qrPaymentFilters.description)
      && matchAmountFilter(row.credit || 0, qrPaymentFilters.credit, formatCurrency)
      && matchAmountFilter(row.debit || 0, qrPaymentFilters.debit, formatCurrency)
      && matchSaleHistoryDateFilter(row.date, qrPaymentFilters.date)
    )),
    [allQrPayments, qrPaymentFilters],
  );

  const filteredManualExpenses = useMemo(
    () => (manualExpenses || []).filter((row) => (
      matchTextFilter(row.code, manualExpenseFilters.code)
      && matchTextFilter(row.accountTitle, manualExpenseFilters.accountTitle)
      && matchTextFilter(row.description, manualExpenseFilters.description)
      && matchTextFilter(row.category || 'Other', manualExpenseFilters.category)
      && matchTextFilter(row.branch, manualExpenseFilters.branch)
      && matchAmountFilter(row.amount, manualExpenseFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(row.date, manualExpenseFilters.date)
    )),
    [manualExpenses, manualExpenseFilters],
  );

  const expenseCategoryOptions = useMemo(
    () => getExpenseCategoryOptions(gymId, manualExpenses),
    [gymId, expenseCategories, manualExpenses],
  );

  const groupedManualExpenses = useMemo(() => {
    const byCategory = new Map();
    (filteredManualExpenses || []).forEach((row) => {
      const category = String(row.category || 'Other').trim() || 'Other';
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category).push(row);
    });

    const ordered = [];
    const seen = new Set();
    expenseCategoryOptions.forEach((category) => {
      const key = category.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const matchedKey = [...byCategory.keys()].find((k) => k.toLowerCase() === key);
      const items = matchedKey ? byCategory.get(matchedKey) : [];
      if (matchedKey) byCategory.delete(matchedKey);
      ordered.push({
        category,
        rows: [...(items || [])].sort(compareExpenseCodes),
        total: (items || []).reduce((s, r) => s + (Number(r.amount) || 0), 0),
      });
    });

    byCategory.forEach((rows, category) => {
      ordered.push({
        category,
        rows: [...rows].sort(compareExpenseCodes),
        total: rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
      });
    });

    return ordered;
  }, [filteredManualExpenses, expenseCategoryOptions]);

  const salesEntryRows = useMemo(
    () => buildCoaSalesEntryRowsFromFinance(data),
    [data],
  );

  const filteredSalesEntries = useMemo(
    () => (salesEntryRows || []).filter((row) => (
      matchGymIdFilter(row.gymId, salesEntryFilters.gymId)
      && matchTextFilter(row.member, salesEntryFilters.member)
      && matchTextFilter(row.category, salesEntryFilters.category)
      && matchTextFilter(row.item, salesEntryFilters.item)
      && matchTextFilter(row.method, salesEntryFilters.method)
      && matchAmountFilter(row.amount, salesEntryFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(row.date, salesEntryFilters.date)
    )),
    [salesEntryRows, salesEntryFilters],
  );

  const groupedSalesEntries = useMemo(() => {
    const byCategory = new Map();
    (filteredSalesEntries || []).forEach((row) => {
      const category = String(row.category || 'Membership').trim() || 'Membership';
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category).push(row);
    });

    const ordered = [];
    const seen = new Set();
    SALE_CATEGORY_ORDER.forEach((category) => {
      const key = category.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const rows = byCategory.get(category) || [];
      byCategory.delete(category);
      ordered.push({
        category,
        rows,
        total: rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
      });
    });

    byCategory.forEach((rows, category) => {
      ordered.push({
        category,
        rows,
        total: rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
      });
    });

    return ordered;
  }, [filteredSalesEntries]);

  const filteredExpenses = useMemo(
    () => (data.expenses || []).filter((row) => (
      matchTextFilter(row.description, expenseFilters.description)
      && matchTextFilter(row.category, expenseFilters.category)
      && matchTextFilter(row.branch, expenseFilters.branch)
      && matchAmountFilter(row.amount, expenseFilters.amount, formatCurrency)
      && matchSaleHistoryDateFilter(row.date, expenseFilters.date)
      && matchTextFilter(row.status, expenseFilters.status)
    )),
    [data.expenses, expenseFilters],
  );

  const zReportPayments = useMemo(
    () => financePaymentPool(data),
    [data.payments, data.guestPassIncome],
  );

  const zReportExpenses = useMemo(() => {
    const apiExpenses = data.expenses || [];
    const coaExpenses = coaZExpenses || [];
    const localExpenses = (manualExpenses || []).map((e) => ({
      id: e.id,
      description: e.description || e.accountTitle || 'Expense',
      category: e.category || 'Other',
      amount: Number(e.amount) || 0,
      date: e.date,
      status: 'paid',
      branch: '',
      method: e.method || e.paymentMethod || 'cash',
      paymentMethod: e.paymentMethod || e.method || 'cash',
    }));
    const seen = new Set();
    const out = [];
    [...coaExpenses, ...apiExpenses, ...localExpenses].forEach((e) => {
      const key = String(e.id || `${e.description}-${e.amount}-${e.date}`);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(e);
    });
    return out;
  }, [data.expenses, manualExpenses, coaZExpenses]);

  const zReport = useMemo(
    () => buildZReport({
      date: zReportDate,
      payments: zReportPayments,
      guestPassIncome: data.guestPassIncome || [],
      expenses: zReportExpenses,
      gymId,
      manualExpenses: [],
    }),
    [
      zReportDate,
      zReportPayments,
      data.guestPassIncome,
      zReportExpenses,
      gymId,
    ],
  );

  const filteredMemberLedgers = useMemo(
    () => (data.memberLedgers || []).filter((m) => (
      matchGymIdFilter(m.gymId, memberFilters.gymId)
      && matchTextFilter(m.name, memberFilters.name)
      && matchTextFilter(m.branch, memberFilters.branch)
      && matchTextFilter(m.status, memberFilters.status)
      && matchAmountFilter(m.profitLoss?.netContribution, memberFilters.netPnl, formatCurrency)
    )),
    [data.memberLedgers, memberFilters],
  );

  const filteredAccounts = useMemo(
    () => accountRows.filter((account) => (
      matchTextFilter(account.name, accountFilters.name)
      && matchTextFilter(account.ledgerGroup === 'cash' ? 'Cash' : 'Bank', accountFilters.method)
      && matchTextFilter(account.side === 'cash' ? '—' : account.side, accountFilters.side)
      && matchAmountFilter(account.displayAmount, accountFilters.amount, formatCurrency)
      && matchTextFilter(account.description, accountFilters.description)
      && matchTextFilter(account.entryCount, accountFilters.entries)
      && matchAmountFilter(account.totalDebit, accountFilters.totalDebit, formatCurrency)
      && matchAmountFilter(account.totalCredit, accountFilters.totalCredit, formatCurrency)
      && matchAmountFilter(account.balance, accountFilters.balance, formatCurrency)
    )),
    [accountRows, accountFilters],
  );

  const filteredCashAccounts = useMemo(
    () => filteredAccounts.filter((a) => a.ledgerGroup === 'cash'),
    [filteredAccounts],
  );

  const filteredBankAccounts = useMemo(
    () => filteredAccounts.filter((a) => a.ledgerGroup !== 'cash'),
    [filteredAccounts],
  );

  const cashAccountsTotal = useMemo(
    () => filteredCashAccounts.reduce((sum, a) => sum + (Number(a.displayAmount) || 0), 0),
    [filteredCashAccounts],
  );

  const bankAccountsTotal = useMemo(
    () => filteredBankAccounts.reduce((sum, a) => sum + (Number(a.displayAmount) || 0), 0),
    [filteredBankAccounts],
  );

  const filteredJournalEntries = useMemo(
    () => journalEntries.filter((row) => (
      matchSaleHistoryDateFilter(row.date, journalFilters.date)
      && matchTextFilter(row.account, journalFilters.account)
      && matchTextFilter(row.description, journalFilters.description)
      && matchGymIdFilter(row.gymId, journalFilters.gymId)
      && matchTextFilter(row.method, journalFilters.method)
      && matchAmountFilter(row.debit || 0, journalFilters.debit, formatCurrency)
      && matchAmountFilter(row.credit || 0, journalFilters.credit, formatCurrency)
      && matchTextFilter(row.ref, journalFilters.ref)
    )),
    [journalEntries, journalFilters],
  );

  const filteredExpenseLedgerEntries = useMemo(
    () => expenseLedgerEntries.filter((row) => (
      matchSaleHistoryDateFilter(row.date, expenseLedgerFilters.date)
      && matchTextFilter(row.accountCode || row.ref, expenseLedgerFilters.code)
      && matchTextFilter(row.account, expenseLedgerFilters.account)
      && matchTextFilter(row.category, expenseLedgerFilters.category)
      && matchTextFilter(row.description, expenseLedgerFilters.description)
      && matchAmountFilter(row.debit || 0, expenseLedgerFilters.amount, formatCurrency)
    )),
    [expenseLedgerEntries, expenseLedgerFilters],
  );

  const journalTotals = useMemo(() => ({
    debit: filteredJournalEntries.reduce((sum, row) => sum + (Number(row.debit) || 0), 0),
    credit: filteredJournalEntries.reduce((sum, row) => sum + (Number(row.credit) || 0), 0),
  }), [filteredJournalEntries]);

  const filteredExpenseLedgerTotals = useMemo(() => ({
    debit: filteredExpenseLedgerEntries.reduce((sum, row) => sum + (Number(row.debit) || 0), 0),
  }), [filteredExpenseLedgerEntries]);

  const filteredMonthly = useMemo(
    () => (data.monthlyMembershipLedger || []).filter((row) => (
      matchTextFilter(row.label, monthlyFilters.label)
      && matchTextFilter(row.paymentCount, monthlyFilters.paymentCount)
      && matchTextFilter(row.newMemberships, monthlyFilters.newMemberships)
      && matchAmountFilter(row.revenue, monthlyFilters.revenue, formatCurrency)
      && matchTextFilter(row.packages?.length
          ? row.packages.map((p) => `${p.name}: ${formatCurrency(p.amount)}`).join(' · ')
          : '—', monthlyFilters.packages)
    )),
    [data.monthlyMembershipLedger, monthlyFilters],
  );

  const handleMemberFilterChange = useCallback((key, value) => {
    updateMemberFilter(key, value);
    setSelectedMemberId('');
  }, [updateMemberFilter]);

  const memberMatches = filteredMemberLedgers;

  const selectedMember = useMemo(() => {
    if (selectedMemberId) {
      return (data.memberLedgers || []).find((m) => m.memberId === selectedMemberId) || null;
    }
    if (memberMatches.length === 1) return memberMatches[0];
    return null;
  }, [data.memberLedgers, selectedMemberId, memberMatches]);

  const activeAccount = useMemo(
    () => accountRows.find((a) => a.code === selectedAccount) || accountRows[0],
    [accountRows, selectedAccount],
  );

  const filteredCategoryEntries = useMemo(
    () => (activeAccount?.entries || []).filter((entry) => (
      matchSaleHistoryDateFilter(entry.date, categoryEntryFilters.date)
      && matchTextFilter(entry.description, categoryEntryFilters.description)
      && matchGymIdFilter(entry.gymId, categoryEntryFilters.gymId)
      && matchAmountFilter(entry.debit || 0, categoryEntryFilters.debit, formatCurrency)
      && matchAmountFilter(entry.credit || 0, categoryEntryFilters.credit, formatCurrency)
      && matchTextFilter(entry.ref, categoryEntryFilters.ref)
    )),
    [activeAccount, categoryEntryFilters],
  );

  const chartData = useMemo(() => ({
    labels: data.revenueChartData?.labels || [],
      datasets: [{
        label: 'Revenue',
      data: data.revenueChartData?.values || [],
        backgroundColor: 'rgba(148, 255, 0, 0.7)',
        borderColor: NEON_GREEN,
      borderWidth: 1,
      borderRadius: 6,
    }],
  }), [data.revenueChartData]);

  const memberContributionRows = useMemo(() => (
    (data.memberLedgers || [])
      .slice()
      .sort((a, b) => (b.profitLoss?.netContribution || 0) - (a.profitLoss?.netContribution || 0))
  ), [data.memberLedgers]);

  const filteredPnlMembers = useMemo(
    () => memberContributionRows.filter((m) => (
      matchGymIdFilter(m.gymId, pnlMemberFilters.gymId)
      && matchTextFilter(m.name, pnlMemberFilters.name)
      && matchAmountFilter(m.profitLoss?.revenue || 0, pnlMemberFilters.revenue, formatCurrency)
      && matchAmountFilter(m.profitLoss?.refunds || 0, pnlMemberFilters.refunds, formatCurrency)
      && matchAmountFilter(m.profitLoss?.outstanding || 0, pnlMemberFilters.outstanding, formatCurrency)
      && matchAmountFilter(m.profitLoss?.netContribution || 0, pnlMemberFilters.netContribution, formatCurrency)
    )),
    [memberContributionRows, pnlMemberFilters],
  );

  const filteredZPackages = useMemo(
    () => (zReport.packageSales || []).filter((row) => (
      matchTextFilter(row.package, zPackageFilters.package)
      && matchTextFilter(row.count, zPackageFilters.count)
      && matchAmountFilter(row.credit, zPackageFilters.credit, formatCurrency)
      && matchAmountFilter(row.debit, zPackageFilters.debit, formatCurrency)
    )),
    [zReport.packageSales, zPackageFilters],
  );

  const filteredZMethods = useMemo(
    () => (zReport.collectionsByMethod || []).filter((row) => (
      matchTextFilter(row.method, zMethodFilters.method)
      && matchTextFilter(row.count, zMethodFilters.count)
      && matchAmountFilter(row.amount, zMethodFilters.amount, formatCurrency)
    )),
    [zReport.collectionsByMethod, zMethodFilters],
  );

  const filteredZExpenses = useMemo(
    () => (zReport.expenses || []).filter((e) => (
      matchTextFilter(e.description, zExpenseFilters.description)
      && matchTextFilter(e.category, zExpenseFilters.category)
      && matchAmountFilter(e.amount, zExpenseFilters.amount, formatCurrency)
    )),
    [zReport.expenses, zExpenseFilters],
  );

  const filteredMemberMemberships = useMemo(
    () => (selectedMember?.memberships || []).filter((m) => (
      matchTextFilter(m.package, memberMembershipFilters.package)
      && matchSaleHistoryDateFilter(m.start, memberMembershipFilters.start)
      && matchSaleHistoryDateFilter(m.expiry, memberMembershipFilters.expiry)
      && matchTextFilter(m.frozen ? 'frozen' : m.status, memberMembershipFilters.status)
    )),
    [selectedMember, memberMembershipFilters],
  );

  const filteredMemberLedgerEntries = useMemo(
    () => (selectedMember?.ledgerEntries || []).filter((entry) => (
      matchSaleHistoryDateFilter(entry.date, memberLedgerFilters.date)
      && matchTextFilter(entry.type, memberLedgerFilters.type)
      && matchTextFilter(entry.description, memberLedgerFilters.description)
      && matchAmountFilter(entry.debit || 0, memberLedgerFilters.debit, formatCurrency)
      && matchAmountFilter(entry.credit || 0, memberLedgerFilters.credit, formatCurrency)
      && matchAmountFilter(entry.balance, memberLedgerFilters.balance, formatCurrency)
      && matchTextFilter(entry.status, memberLedgerFilters.status)
    )),
    [selectedMember, memberLedgerFilters],
  );

  const memberPnlChartData = useMemo(() => {
    if (!selectedMember) return null;
    const pnl = selectedMember.profitLoss;
    return {
      labels: ['Revenue', 'Membership', 'Guest Passes', 'Refunds', 'Outstanding', 'Net'],
      datasets: [{
        label: 'Amount',
        data: [
          pnl.revenue,
          pnl.membershipIncome,
          pnl.guestPassIncome,
          pnl.refunds,
          pnl.outstanding,
          pnl.netContribution,
        ],
        backgroundColor: [
          'rgba(148, 255, 0, 0.7)',
          'rgba(148, 255, 0, 0.55)',
          'rgba(34, 197, 94, 0.65)',
          'rgba(239, 68, 68, 0.65)',
          'rgba(245, 158, 11, 0.65)',
          'rgba(148, 255, 0, 0.9)',
        ],
        borderRadius: 6,
      }],
    };
  }, [selectedMember]);

  const financeExportReport = useMemo(() => {
    if (tab === 'accounts') {
      const cashRows = filteredManualCashPayments.map((p) => [
        'Cash',
        p.code,
        p.gymId || '—',
        p.accountTitle,
        p.description || '—',
        formatCurrency(p.payment),
        '—',
        p.date || '—',
      ]);
      const bankRows = filteredManualBankPayments.map((p) => [
        'Bank',
        p.code,
        p.gymId || '—',
        p.accountTitle,
        p.description || '—',
        p.credit ? formatCurrency(p.credit) : '—',
        p.debit ? formatCurrency(p.debit) : '—',
        p.date || '—',
      ]);
      const cardRows = filteredManualCardPayments.map((p) => [
        'Card',
        p.code,
        p.gymId || '—',
        p.accountTitle,
        p.description || '—',
        p.credit ? formatCurrency(p.credit) : '—',
        p.debit ? formatCurrency(p.debit) : '—',
        p.date || '—',
      ]);
      const qrRows = filteredManualQrPayments.map((p) => [
        'QR',
        p.code,
        p.gymId || '—',
        p.accountTitle,
        p.description || '—',
        p.credit ? formatCurrency(p.credit) : '—',
        p.debit ? formatCurrency(p.debit) : '—',
        p.date || '—',
      ]);
      const expenseRows = filteredManualExpenses.map((p) => [
        `Expense${p.category ? ` (${p.category})` : ''}`,
        p.code,
        p.branch || '—',
        p.accountTitle,
        p.description || '—',
        formatCurrency(p.amount),
        '—',
        p.date || '—',
      ]);
      return {
        title: 'Chart of Accounts',
        slug: 'finance-accounts',
        headers: ['Type', 'Code', 'Gym ID / Branch', 'Account Title', 'Description', 'Credit/Payment', 'Debit', 'Date'],
        rows: [...cashRows, ...bankRows, ...cardRows, ...qrRows, ...expenseRows],
        summary: [
          `Cash entries: ${filteredManualCashPayments.length}`,
          `Bank entries: ${filteredManualBankPayments.length}`,
          `Card entries: ${filteredManualCardPayments.length}`,
          `QR entries: ${filteredManualQrPayments.length}`,
          `Expense entries: ${filteredManualExpenses.length}`,
        ],
      };
    }

    if (tab === 'journal') {
      if (showExpenseLedger) {
        return {
          title: `Expense Ledger${expenseLedgerCategory !== 'all' ? ` — ${expenseLedgerCategory}` : ''}`,
          slug: 'finance-expense-ledger',
          headers: ['Date', 'Code', 'Account Title', 'Category', 'Description', 'Amount'],
          rows: filteredExpenseLedgerEntries.map((row) => [
            row.date,
            row.ref,
            row.account,
            row.category || '—',
            row.description || '—',
            row.debit ? formatCurrency(row.debit) : '—',
          ]),
          summary: [
            `Entries: ${filteredExpenseLedgerEntries.length}`,
            `Total: ${formatCurrency(filteredExpenseLedgerTotals.debit)}`,
          ],
        };
      }
      const typeLabel = journalQuery.type === 'all'
        ? 'All'
        : String(journalQuery.type || 'all').replace(/^\w/, (c) => c.toUpperCase());
      const memberLabel = journalQuery.memberOrGym || 'All members / gym IDs';
      return {
        title: `Journal Ledger — ${typeLabel} — ${memberLabel}`,
        slug: 'finance-journal-ledger',
        headers: ['Date', 'Code', 'Serial No', 'Account Title', 'Member', 'Method', 'Debit', 'Credit'],
        rows: filteredJournalEntries.map((row) => [
          row.date,
          row.ref,
          row.gymId || '—',
          row.account,
          row.description,
          row.method || '—',
          row.debit ? formatCurrency(row.debit) : '—',
          row.credit ? formatCurrency(row.credit) : '—',
        ]),
        summary: [
          `Entries: ${filteredJournalEntries.length}`,
          `Total Debit: ${formatCurrency(journalTotals.debit)}`,
          `Total Credit: ${formatCurrency(journalTotals.credit)}`,
        ],
      };
    }

    if (tab === 'member') {
      if (selectedMember) {
        return {
          title: `Member Ledger — ${selectedMember.name}${selectedMember.gymId ? ` (${selectedMember.gymId})` : ''}`,
          slug: 'finance-member-ledger',
          headers: ['Date', 'Type', 'Description', 'Debit', 'Credit', 'Balance', 'Status'],
          rows: (filteredMemberLedgerEntries.length
            ? filteredMemberLedgerEntries
            : (selectedMember.ledgerEntries || [])
          ).map((e) => [
            e.date || '—',
            e.type || '—',
            e.description || '—',
            e.debit ? formatCurrency(e.debit) : '—',
            e.credit ? formatCurrency(e.credit) : '—',
            formatCurrency(e.balance || 0),
            e.status || '—',
          ]),
          summary: [
            `Revenue: ${formatCurrency(selectedMember.profitLoss?.revenue || 0)}`,
            `Net contribution: ${formatCurrency(selectedMember.profitLoss?.netContribution || 0)}`,
          ],
        };
      }
      return {
        title: 'Member Ledger',
        slug: 'finance-members',
        headers: ['Serial No', 'Name', 'Branch', 'Status', 'Net P&L'],
        rows: filteredMemberLedgers.map((m) => [
          m.gymId || '—',
          m.name || '—',
          m.branch || '—',
          m.status || '—',
          formatCurrency(m.profitLoss?.netContribution || 0),
        ]),
        summary: [`Members: ${filteredMemberLedgers.length}`],
      };
    }

    if (tab === 'zreport') {
      return {
        title: `Z Report — ${zReport.date}`,
        slug: 'finance-z-report',
        headers: ['Package', 'Sales #', 'Credit (Package)', 'Debit (Collected)'],
        rows: filteredZPackages.map((row) => [
          row.package,
          String(row.count ?? 0),
          formatCurrency(row.credit),
          formatCurrency(row.debit),
        ]),
        summary: [
          `Total sales (credit): ${formatCurrency(zReport.totalSalesCredit)}`,
          `Total collected (debit): ${formatCurrency(zReport.totalCollectedDebit)}`,
          `Payment pending: ${formatCurrency(zReport.paymentPending || 0)}`,
          `Day expenses: ${formatCurrency(zReport.expenseTotal)}`,
          `Final day result: ${formatCurrency(zReport.netDayResult)}`,
          `Cash closing: ${formatCurrency(zReport.cashSummary?.closing || 0)}`,
          `Bank closing: ${formatCurrency(zReport.bankSummary?.closing || 0)}`,
          ...filteredZMethods.map((m) => `${m.method}: ${formatCurrency(m.amount)} (${m.count})`),
        ],
      };
    }

    return {
      title: 'Chart of Accounts',
      slug: 'finance-accounts',
      headers: ['Type', 'Code', 'Account Title', 'Description', 'Credit/Payment', 'Debit', 'Date'],
      rows: [],
      summary: [],
    };
  }, [
    tab,
    accountRows,
    filteredCashPayments,
    filteredManualCashPayments,
    filteredManualBankPayments,
    filteredManualCardPayments,
    filteredManualQrPayments,
    filteredManualExpenses,
    filteredBankPayments,
    filteredJournalEntries,
    journalTotals,
    journalQuery,
    showExpenseLedger,
    expenseLedgerCategory,
    filteredExpenseLedgerEntries,
    filteredExpenseLedgerTotals,
    selectedMember,
    filteredMemberLedgerEntries,
    filteredMemberLedgers,
    zReport,
    filteredZPackages,
    filteredZMethods,
  ]);

  const runFinanceExport = (action) => {
    const runners = {
      print: printFinanceList,
      csv: exportFinanceCsv,
      pdf: exportFinancePdf,
      excel: exportFinanceExcel,
      export: exportFinance,
    };
    runners[action]?.(financeExportReport);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      setSavingExpense(true);
      const created = await createExpense({
        ...expenseForm,
        amount: Number(expenseForm.amount) || 0,
      }, gymId);
      toast.showSaved('Expense');
      setShowExpenseForm(false);
      setExpenseForm({
        description: '',
        category: 'Other',
        amount: '',
        date: new Date().toISOString().slice(0, 10),
        branch: '',
        status: 'paid',
      });
      // Show immediately even if a full reload is slow
      setData((prev) => ({
        ...prev,
        expenses: [created, ...(prev.expenses || []).filter((row) => row.id !== created.id)],
        revenueSummary: {
          ...prev.revenueSummary,
          expenses: (Number(prev.revenueSummary?.expenses) || 0) + (Number(created.amount) || 0),
        },
      }));
      await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to save expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await deleteExpense(id, gymId);
      toast.showDeleted('Expense');
      await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete expense');
    }
  };

  const emptyAccountForm = () => ({
    name: '',
    method: 'cash',
    side: 'debit',
    amount: '',
    code: '',
    description: '',
  });

  const handleOpenAccountForm = () => {
    if (showAccountForm) {
      setShowAccountForm(false);
      setEditingAccountCode('');
      setAccountForm(emptyAccountForm());
      return;
    }
    setEditingAccountCode('');
    setAccountForm({
      ...emptyAccountForm(),
      method: 'cash',
      side: 'debit',
      code: suggestNextAccountCode('debit', gymId),
    });
    setShowAccountForm(true);
  };

  const handleEditAccount = (account) => {
    if (!account?.isCustom) {
      toast.showError('Built-in accounts cannot be edited.');
      return;
    }
    const method = normalizeAccountMethod(account);
    setEditingAccountCode(account.code);
    setAccountForm({
      name: account.name || '',
      method,
      side: method === 'cash'
        ? 'debit'
        : (account.side === 'credit' || account.side === 'debit'
          ? account.side
          : 'debit'),
      // Leave amount blank so the next save adds a new entry (amounts sum)
      amount: '',
      code: account.code || '',
      description: '',
    });
    setShowAccountForm(true);
  };

  const handleAccountMethodChange = (method) => {
    setAccountForm((f) => {
      const nextSide = method === 'cash' ? 'debit' : (f.side === 'credit' ? 'credit' : 'debit');
      return {
        ...f,
        method,
        side: nextSide,
        code: editingAccountCode ? f.code : suggestNextAccountCode(nextSide, gymId),
      };
    });
  };

  const handleAccountSideChange = (side) => {
    setAccountForm((f) => ({
      ...f,
      side,
      code: editingAccountCode ? f.code : suggestNextAccountCode(side, gymId),
    }));
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      setSavingAccount(true);
      const payload = {
        ...accountForm,
        method: accountForm.method || 'cash',
        side: accountForm.method === 'cash' ? 'debit' : accountForm.side,
        amount: Number(accountForm.amount),
      };
      if (editingAccountCode) {
        await updateChartAccount(editingAccountCode, payload, gymId);
        toast.showUpdated('Account entry added');
      } else {
        const created = await createChartAccount(payload, gymId);
        if (created?.appended) {
          toast.showUpdated('Account entry added');
        } else {
          toast.showSaved('Chart account');
        }
      }
      setShowAccountForm(false);
      setEditingAccountCode('');
      setAccountForm(emptyAccountForm());
      await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to save account');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleDeleteAccount = async () => {
    const code = deleteAccountTarget?.code;
    if (!code) return;
    try {
      await removeChartAccount(code, gymId);
      if (editingAccountCode === code) {
        setShowAccountForm(false);
        setEditingAccountCode('');
        setAccountForm(emptyAccountForm());
      }
      setDeleteAccountTarget(null);
      toast.showDeleted('Chart account');
      await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete account');
    }
  };

  const emptyCashPaymentForm = () => ({
    code: suggestNextCashPaymentCode(gymId),
    accountTitle: '',
    description: '',
    payment: '',
    date: localDateKey(),
  });

  const handleOpenCashPaymentForm = () => {
    if (showCashPaymentForm) {
      setShowCashPaymentForm(false);
      setCashPaymentForm(emptyCashPaymentForm());
      return;
    }
    setCashPaymentForm(emptyCashPaymentForm());
    setShowCashPaymentForm(true);
  };

  const handleSaveCashPayment = (e) => {
    e.preventDefault();
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      setSavingCashPayment(true);
      addManualCashPayment(gymId, {
        ...cashPaymentForm,
        payment: Number(cashPaymentForm.payment),
      });
      setManualCashPayments(loadManualCashPayments(gymId));
      setShowCashPaymentForm(false);
      setCashPaymentForm(emptyCashPaymentForm());
      toast.showSaved('Cash account entry');
    } catch (err) {
      toast.showError(err.message || 'Failed to save cash account entry');
    } finally {
      setSavingCashPayment(false);
    }
  };

  const handleDeleteCashPayment = (id) => {
    try {
      deleteManualCashPayment(gymId, id);
      setManualCashPayments(loadManualCashPayments(gymId));
      toast.showDeleted('Cash account entry');
    } catch (err) {
      toast.showError(err.message || 'Failed to delete cash account entry');
    }
  };

  const emptyBankPaymentForm = () => ({
    code: suggestNextBankPaymentCode(gymId),
    accountTitle: '',
    description: '',
    credit: '',
    debit: '',
    date: localDateKey(),
  });

  const handleOpenBankPaymentForm = () => {
    if (showBankPaymentForm) {
      setShowBankPaymentForm(false);
      setBankPaymentForm(emptyBankPaymentForm());
      return;
    }
    setBankPaymentForm(emptyBankPaymentForm());
    setShowBankPaymentForm(true);
  };

  const handleSaveBankPayment = (e) => {
    e.preventDefault();
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      setSavingBankPayment(true);
      addManualBankPayment(gymId, {
        ...bankPaymentForm,
        credit: Number(bankPaymentForm.credit) || 0,
        debit: Number(bankPaymentForm.debit) || 0,
      });
      setManualBankPayments(loadManualBankPayments(gymId));
      setShowBankPaymentForm(false);
      setBankPaymentForm(emptyBankPaymentForm());
      toast.showSaved('Bank account entry');
    } catch (err) {
      toast.showError(err.message || 'Failed to save bank payment');
    } finally {
      setSavingBankPayment(false);
    }
  };

  const handleDeleteBankPayment = (id) => {
    try {
      deleteManualBankPayment(gymId, id);
      setManualBankPayments(loadManualBankPayments(gymId));
      toast.showDeleted('Bank account entry');
    } catch (err) {
      toast.showError(err.message || 'Failed to delete bank account entry');
    }
  };

  const emptyCardPaymentForm = () => ({
    code: suggestNextCardPaymentCode(gymId),
    accountTitle: '',
    description: '',
    credit: '',
    debit: '',
    date: localDateKey(),
  });

  const handleOpenCardPaymentForm = () => {
    if (showCardPaymentForm) {
      setShowCardPaymentForm(false);
      setCardPaymentForm(emptyCardPaymentForm());
      return;
    }
    setCardPaymentForm(emptyCardPaymentForm());
    setShowCardPaymentForm(true);
  };

  const handleSaveCardPayment = (e) => {
    e.preventDefault();
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      setSavingCardPayment(true);
      addManualCardPayment(gymId, {
        ...cardPaymentForm,
        credit: Number(cardPaymentForm.credit) || 0,
        debit: Number(cardPaymentForm.debit) || 0,
      });
      setManualCardPayments(loadManualCardPayments(gymId));
      setShowCardPaymentForm(false);
      setCardPaymentForm(emptyCardPaymentForm());
      toast.showSaved('Card payment');
    } catch (err) {
      toast.showError(err.message || 'Failed to save card payment');
    } finally {
      setSavingCardPayment(false);
    }
  };

  const handleDeleteCardPayment = (id) => {
    try {
      deleteManualCardPayment(gymId, id);
      setManualCardPayments(loadManualCardPayments(gymId));
      toast.showDeleted('Card payment');
    } catch (err) {
      toast.showError(err.message || 'Failed to delete card payment');
    }
  };

  const emptyManualExpenseForm = () => {
    const category = expenseCategoryOptions[0] || 'Other';
    const branchOptions = data.branchOptions || [];
    return {
      code: suggestNextExpenseCode(gymId, category, manualExpenses),
      accountTitle: '',
      description: '',
      category,
      branch: branchOptions[0] || '',
      amount: '',
      date: localDateKey(),
      method: 'cash',
    };
  };

  const handleExpenseCategoryChange = (category) => {
    setManualExpenseForm((f) => ({
      ...f,
      category,
      code: suggestNextExpenseCode(gymId, category, manualExpenses),
    }));
  };

  const handleOpenManualExpenseForm = () => {
    if (showManualExpenseForm) {
      setShowManualExpenseForm(false);
      setManualExpenseForm(emptyManualExpenseForm());
      return;
    }
    if (!expenseCategories.length && !expenseCategoryOptions.length) {
      toast.showError('Add a category first. Codes start at E1000, E2000, E3000…');
      setShowExpenseCategoryForm(true);
      return;
    }
    setShowExpenseCategoryForm(false);
    setManualExpenseForm(emptyManualExpenseForm());
    setShowManualExpenseForm(true);
  };

  const handleOpenExpenseCategoryForm = () => {
    if (showExpenseCategoryForm) {
      setShowExpenseCategoryForm(false);
      setExpenseCategoryName('');
      return;
    }
    setShowManualExpenseForm(false);
    setExpenseCategoryName('');
    setShowExpenseCategoryForm(true);
  };

  const handleSaveExpenseCategory = async (e) => {
    e.preventDefault();
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      setSavingExpenseCategory(true);
      // Assign code base locally (E1000 / E2000…), then require Supabase persist
      const created = addExpenseCategory(gymId, expenseCategoryName);
      try {
        await createExpenseCategoryRemote({
          name: created.category,
          codeBase: created.codeBase,
        }, gymId);
      } catch (remoteErr) {
        deleteExpenseCategory(gymId, created.category, { deleteExpenses: false });
        throw remoteErr;
      }
      setExpenseCategories(loadExpenseCategories(gymId));
      setShowExpenseCategoryForm(false);
      setExpenseCategoryName('');
      toast.showSaved(`Category · codes ${created.startCode}+`);
    } catch (err) {
      toast.showError(err.message || 'Failed to save category');
    } finally {
      setSavingExpenseCategory(false);
    }
  };

  const handleSaveManualExpense = async (e) => {
    e.preventDefault();
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      setSavingManualExpense(true);
      if ((data.branchOptions || []).length > 0 && !String(manualExpenseForm.branch || '').trim()) {
        toast.showError('Select a branch for this expense.');
        return;
      }
      const payload = {
        ...manualExpenseForm,
        amount: Number(manualExpenseForm.amount),
      };
      try {
        const code = String(payload.code || '').trim()
          || suggestNextExpenseCode(gymId, payload.category, manualExpenses);
        const created = await createExpense({
          accountTitle: payload.accountTitle,
          description: payload.description,
          category: payload.category,
          branch: payload.branch,
          amount: payload.amount,
          date: payload.date,
          code,
          status: 'paid',
          method: payload.method === 'bank' ? 'bank' : 'cash',
          paymentMethod: payload.method === 'bank' ? 'bank' : 'cash',
        }, gymId);
        const mapped = mapApiExpenseToManual(created);
        setManualExpenses((prev) => mergeManualExpenses([mapped], prev));
        setData((prev) => ({
          ...prev,
          expenses: [created, ...(prev.expenses || []).filter((row) => row.id !== created.id)],
          revenueSummary: {
            ...prev.revenueSummary,
            expenses: (Number(prev.revenueSummary?.expenses) || 0) + (Number(created.amount) || 0),
          },
        }));
      } catch (remoteErr) {
        // Fallback to localStorage if expenses table / columns missing
        if (!/table not found|does not exist|schema cache/i.test(remoteErr.message || '')) {
          throw remoteErr;
        }
        addManualExpense(gymId, payload);
        setManualExpenses(loadManualExpenses(gymId));
        toast.showError(remoteErr.message || 'Expense saved on this device only. Run the SQL script to persist after deploy.');
        setShowManualExpenseForm(false);
        setManualExpenseForm(emptyManualExpenseForm());
        return;
      }
      setShowManualExpenseForm(false);
      setManualExpenseForm(emptyManualExpenseForm());
      toast.showSaved('Expense');
      await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to save expense');
    } finally {
      setSavingManualExpense(false);
    }
  };

  const handleDeleteManualExpense = async (id) => {
    try {
      const isLocalId = String(id || '').startsWith('EXP-');
      if (isLocalId) {
        deleteManualExpense(gymId, id);
        setManualExpenses(loadManualExpenses(gymId));
      } else {
        await deleteExpense(id, gymId);
        setManualExpenses((prev) => (prev || []).filter((row) => row.id !== id));
        setData((prev) => ({
          ...prev,
          expenses: (prev.expenses || []).filter((row) => row.id !== id),
        }));
      }
      toast.showDeleted('Expense');
      if (!isLocalId) await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete expense');
      throw err;
    }
  };

  const requestDeleteManualExpense = (row) => {
    if (!row?.id) return;
    setDeleteConfirm({
      kind: 'expense',
      title: 'Delete Expense',
      confirmLabel: 'Delete Expense',
      label: row.accountTitle || row.description || 'this expense',
      detail: row.code || '',
      payload: row.id,
    });
  };

  const handleDeleteExpenseCategory = async (categoryName) => {
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    try {
      try {
        await deleteExpenseCategoryRemote(categoryName, gymId, { deleteExpenses: true });
      } catch (remoteErr) {
        if (!/table not found|does not exist|schema cache/i.test(remoteErr.message || '')) {
          throw remoteErr;
        }
      }
      deleteExpenseCategory(gymId, categoryName, { deleteExpenses: true });
      setExpenseCategories(loadExpenseCategories(gymId));
      setManualExpenses((prev) => (prev || []).filter(
        (row) => String(row.category || '').trim().toLowerCase() !== String(categoryName || '').trim().toLowerCase(),
      ));
      toast.showDeleted('Category');
      await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete category');
      throw err;
    }
  };

  const requestDeleteExpenseCategory = (categoryName) => {
    if (!gymId) {
      toast.showError('No gym linked to this account.');
      return;
    }
    const count = (manualExpenses || []).filter(
      (row) => String(row.category || '').trim().toLowerCase() === String(categoryName || '').trim().toLowerCase(),
    ).length;
    setDeleteConfirm({
      kind: 'category',
      title: 'Delete Category',
      confirmLabel: 'Delete Category',
      label: categoryName,
      detail: count > 0
        ? `${count} expense${count === 1 ? '' : 's'} will also be deleted`
        : '',
      payload: categoryName,
    });
  };

  const closeDeleteConfirm = () => {
    if (deletingConfirm) return;
    setDeleteConfirm(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeletingConfirm(true);
      if (deleteConfirm.kind === 'expense') {
        await handleDeleteManualExpense(deleteConfirm.payload);
      } else if (deleteConfirm.kind === 'category') {
        await handleDeleteExpenseCategory(deleteConfirm.payload);
      }
      setDeleteConfirm(null);
    } catch {
      // Errors already toasted in handlers
    } finally {
      setDeletingConfirm(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Financial Ledger"
        subtitle="Chart of accounts, journal, vouchers, and Z report"
      />

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading finance ledger...</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="admin-finance-tabs rec-member-toolbar" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`admin-finance-tab${tab === item.id ? ' active' : ''}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'accounts' && (
        <CoaAccountTree
          gymId={gymId}
          liveData={{
            cash: allCashPayments,
            card: allCardPayments,
            bank: allBankPayments,
            qr: allQrPayments,
            salesEntries: salesEntryRows,
          }}
        />
      )}

      {tab === 'journal' && (
        <JournalLedger
          gymId={gymId}
          liveData={{
            cash: allCashPayments,
            card: allCardPayments,
            bank: allBankPayments,
            qr: allQrPayments,
            salesEntries: salesEntryRows,
          }}
        />
      )}

      {VOUCHER_TAB_IDS.has(tab) && (
        <VoucherPage typeId={tab} gymId={gymId} />
      )}

      {tab === 'customer-balance' && (
        <CustomerBalanceList gymId={gymId} />
      )}

      {tab === 'zreport' && (
        <DailyZReport
          embedded
          title="Z Report — End of Day"
          subtitle="Select a date to load that day’s Z Report — Edit to change, Save to keep"
          initialDate={zReportDate}
        />
      )}

      {tab === 'zreport1' && (
        <DailyZReport1
          embedded
          title="Z Report 1 — Excel layout"
          subtitle="Today sale & payment auto; bank/cash/month fields manual"
          initialDate={zReportDate}
        />
      )}

      {memberLedgerTarget && (
        <MemberPnLLedgerModal
          member={memberLedgerTarget}
          onClose={() => setMemberLedgerTarget(null)}
        />
      )}

      {deleteAccountTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteAccountTarget(null)}>
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
          >
            <div className="subscription-modal-header">
              <h2 id="delete-account-title" className="subscription-modal-title">Delete Account</h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={() => setDeleteAccountTarget(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="subscription-form">
              <p className="owner-placeholder-text" style={{ margin: '0 0 20px' }}>
                Delete account <strong>{deleteAccountTarget.name}</strong>
                {deleteAccountTarget.code ? ` (${deleteAccountTarget.code})` : ''}?
                This action cannot be undone.
              </p>
              <div className="subscription-form-actions">
                <button
                  type="button"
                  className="owner-btn owner-btn-sm admin-finance-btn-danger"
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </button>
                <button
                  type="button"
                  className="owner-btn owner-btn-secondary"
                  onClick={() => setDeleteAccountTarget(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="subscription-modal-overlay" onClick={closeDeleteConfirm}>
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="finance-delete-confirm-title"
          >
            <div className="subscription-modal-header">
              <h2 id="finance-delete-confirm-title" className="subscription-modal-title">
                {deleteConfirm.title}
              </h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={closeDeleteConfirm}
                aria-label="Close"
                disabled={deletingConfirm}
              >
                ×
              </button>
            </div>
            <div className="subscription-form">
              <p className="owner-placeholder-text" style={{ margin: '0 0 20px' }}>
                Are you sure you want to delete{' '}
                <strong>{deleteConfirm.label}</strong>
                {deleteConfirm.kind === 'expense' && deleteConfirm.detail
                  ? ` (${deleteConfirm.detail})`
                  : ''}
                ?
                {deleteConfirm.kind === 'category' && deleteConfirm.detail
                  ? ` ${deleteConfirm.detail}.`
                  : ' '}
                This action cannot be undone.
              </p>
              <div className="subscription-form-actions">
                <button
                  type="button"
                  className="owner-btn owner-btn-sm admin-finance-btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deletingConfirm}
                >
                  {deletingConfirm ? 'Deleting…' : deleteConfirm.confirmLabel}
                </button>
                <button
                  type="button"
                  className="owner-btn owner-btn-secondary"
                  onClick={closeDeleteConfirm}
                  disabled={deletingConfirm}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FinanceManagement;
